import React, { Fragment, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { UseFormReturn } from 'react-hook-form';
import { useMutation } from 'react-query';
import { Input, Loader, Tooltip } from 'freemium-ui';
import request from 'utils/request';
import Icons from '../icons';
import Spacer from '../Spacer';
import { Wrapper, Uploadcontainer } from './style';
import { ErrorField } from 'shared/globals';

type UploadLogoProps = {
  form: UseFormReturn;
  label?: string;
  placeholder?: string;
  tipText?: string;
  logoType?: string;
  surveyId?: string | null | undefined;
  fieldId?: string;
  isDisabled?: boolean;
};

const UploadLogo = (props: UploadLogoProps) => {
  const {
    form,
    logoType = 'accounts',
    surveyId = null,
    fieldId = '',
    label,
    placeholder,
    tipText,
    isDisabled,
  } = props;
  const [isWebLink, setIsWebLink] = React.useState(false);
  const [logo, setLogo] = React.useState<any>(null);

  const {
    watch,
    setValue,
    formState: { errors },
    register,
    getValues,
  } = form;
  const setFormValue = (fieldId: any, fieldValue: any) => {
    setValue(fieldId, fieldValue, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
  };
  const {
    mutate: upload,
    isLoading: isUploading,
    isError: uploadError,
  } = useUploadLogo({
    callback: (isSuccess: any, response: any) => {
      if (isSuccess) {
        setLogo(response);
        setFormValue(fieldId, response);
      }
    },
  });

  const onDrop = (files: any) => {
    if (files?.length) {
      let formdata = new FormData();
      formdata.append('file', files[0], files[0].name);
      if (logoType === 'accounts') {
        upload({ payload: formdata, logoType });
      } else {
        if (surveyId) upload({ payload: formdata, surveyId, logoType });
        else {
          getBase64(files[0]).then((base64) => {
            if (logoType === 'survey')
              window.sessionStorage.setItem('base64Logo', base64 as string);
            else
              window.sessionStorage.setItem('base64Favicon', base64 as string);
            setLogo(base64);
            setFormValue(fieldId, base64);
          });
        }
      }
    }
  };

  const {
    getRootProps,
    getInputProps,
    isFocused,
    isDragAccept,
    isDragReject,
    fileRejections,
  } = useDropzone({
    accept: 'image/png, image/jpg, image/jpeg',
    onDrop,
    maxFiles: 1,
    maxSize: 1000000,
    disabled: Boolean(logo) || isUploading,
  });

  const getBase64 = (file: File) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  };

  const handleRemove = () => {
    setLogo(null);
    setFormValue(fieldId, '');
  };

  useEffect(() => {
    if (fieldId && watch(fieldId)) setIsWebLink(true);
  }, [fieldId]);

  // console.log(getValues(), fieldId);

  return (
    <Wrapper>
      <div className="title-cont">
        <div className="label">
          {label}&nbsp;
          {tipText ? (
            <Tooltip
              label={tipText}
              position="right"
              style={{ display: 'block', paddingTop: '5px' }}
            >
              <Icons glyph="info" size={12} viewBox="0 0 16 16" />
            </Tooltip>
          ) : null}
        </div>
        {!isDisabled && !watch(fieldId)?.length ? (
          <div className="toggle-btn" onClick={() => setIsWebLink(!isWebLink)}>
            {isWebLink ? 'Upload logo' : 'Use web link'}
          </div>
        ) : null}
      </div>
      {isWebLink ? (
        <>
          <Input
            autoFocus
            placeholder={'Enter logo url'}
            {...register(fieldId)}
            isErrored={!!errors[fieldId]}
            errorText={errors[fieldId]?.message}
            disabled={getValues().state === 'ACTIVE'}
            endAdornment={
              watch(fieldId) &&
              !errors[fieldId] &&
              getValues().state !== 'ACTIVE' ? (
                <div className="link" onClick={() => handleRemove()}>
                  Remove
                </div>
              ) : null
            }
          />
        </>
      ) : (
        <>
          {logo ? (
            <>
              <Input
                disabled
                value={logo}
                endAdornment={
                  <div className="link" onClick={() => handleRemove()}>
                    Remove
                  </div>
                }
              />
              <Spacer />
            </>
          ) : (
            <>
              <Uploadcontainer
                {...getRootProps()}
                isFocused={isFocused}
                isDragAccept={isDragAccept}
                isDragReject={isDragReject}
                isDisabled={isDisabled}
              >
                {isUploading ? (
                  <Loader />
                ) : (
                  <>
                    <input {...getInputProps()} />
                    <Icons glyph="upload" size={14} viewBox="0 0 14 15" />
                    <Spacer y={0.5} />
                    <div className="link">{placeholder}</div>
                    <p> Maximum file size : 1MB</p>
                  </>
                )}
              </Uploadcontainer>
              {fileRejections.map(({ file, errors }: any) => (
                <Fragment key={file.path}>
                  {errors.length ? (
                    <ErrorField>File size is too large</ErrorField>
                  ) : null}
                </Fragment>
              ))}
              {uploadError ? <ErrorField> Upload error </ErrorField> : null}
            </>
          )}
        </>
      )}
    </Wrapper>
  );
};

export default UploadLogo;

type uploadPayloadType = {
  payload: FormData;
  logoType: string;
  surveyId?: string;
};

export const useUploadLogo = ({ callback }: { callback?: any }) => {
  return useMutation(
    async ({ payload, surveyId, logoType }: uploadPayloadType) => {
      const url =
        logoType === 'accounts'
          ? 'accounts/logo'
          : logoType === 'favicon'
          ? `surveys/${surveyId}/favicon/upload`
          : `surveys/${surveyId}/logo?meta=${logoType === 'meta'}`;
      const data = await request(url, payload, 'POST', {
        'Content-Type': 'multipart/form-data',
      });
      return data;
    },
    {
      onSuccess: (response: any, data: uploadPayloadType) => {
        if (callback) callback(true, response.url, data.logoType);
        return response;
      },
      onError: (error: any) => {
        if (callback) callback(false, error);
      },
    },
  );
};
